/**
 * What Radar did, as opposed to what it was told to do.
 *
 * Source of truth: openwrench-intelligence-service#22,
 * `/api/v1/buyer/agent/executions` and `/procedures/{id}/events`. Field names
 * are the wire names for the same reason the plan's are (see AOPTypes): these
 * documents are read and shown, never rewritten, so a camelCase mapping layer
 * would only be somewhere for them to drift.
 *
 * An execution is one run of one published version against one work order. A
 * work order can have several — it can be reopened — so a run, not a work
 * order, is the thing with a state and a history.
 */

import { AOPChannel, AOPParty, AOPPlan } from './AOPTypes';

/**
 * Where a run is. `paused` is the service's own state now, not something read
 * off the newest event's snapshot — so a held run is a thing that can be asked
 * for rather than inferred.
 */
export type AOPExecutionState = 'running' | 'paused' | 'finished';

/**
 * How a finished run ended. Carried on the `finished` event, not on the run.
 *
 * Three, where a plan's finish step has two: `cancelled` is what a run becomes
 * when somebody stops it from outside, which is not something a plan can name.
 */
export type AOPExecutionOutcome = 'success' | 'failure' | 'cancelled';

export type AOPOverrideAction = 'pause' | 'resume' | 'cancel';

/**
 * Which end of a feed to read from. Both event endpoints take it; they differ
 * in default — a procedure's feed is newest first, one run's is oldest first.
 */
export type AOPEventOrder = 'asc' | 'desc';

/**
 * The fields an event may add to its snapshot. Which ones appear depends on
 * `kind`; all of them are optional here because one type covers every kind.
 *
 * `kind` itself is deliberately a bare string: the service types it as one, so
 * a kind we have never seen is a thing that can arrive, and the UI has to show
 * it rather than fall over.
 */
export interface AOPEventSnapshot {
	/** How many steps the run has entered so far. On every snapshot. */
	step_count: number;
	/**
	 * The step the run is on. `name` matches a step's `name` in the pinned plan;
	 * `description` is the sentence that step carries. Renamed in lockstep with
	 * the plan language — Radar rejects the old keys outright.
	 */
	current_step?: { name: string; description: string };
	/**
	 * Who the run is pointed at. On nearly every snapshot, and it changes when
	 * Radar reassigns, so it is per-event rather than per-run. `id` is the
	 * supplier facility's, which is what the supplier detail route takes.
	 */
	supplier?: { id: string; name: string };
	/** On `started`: when the run gives up regardless of where the plan is. */
	deadline?: string;
	/** Present, and true, only while the run is paused. */
	paused?: boolean;
	/** On the communication events. */
	party?: AOPParty;
	channel?: AOPChannel;
	/** On `waiting`: 0 is the first silent wait, n is after the nth chase. */
	escalation?: number;
	waiting_until?: string;
	/** On `decided` the exit taken; on `finished` or `paused` free text. */
	reason?: string;
	/** On `assigned`: `openwrench` when a person moved it, not Radar. */
	assigned_by?: string;
	/** On `paused`, `resumed`, and a `finished` that a buyer cancelled. */
	requested_by?: string;
	outcome?: AOPExecutionOutcome;
}

export interface AOPEvent {
	/** Starts at 1 within its run. */
	sequence: number;
	kind: string;
	occurred_at: string;
	snapshot: AOPEventSnapshot;
}

/** The same event read across runs, so it has to say which run it came from. */
export interface AOPProcedureEvent extends AOPEvent {
	execution_id: string;
	work_order_id: number;
	/**
	 * The procedure and version the run was following when this happened.
	 *
	 * On the event itself, so a line's step position does not wait on the run
	 * being read: the position is a step's place in the pinned version's plan,
	 * and these name that plan. The run is still read for what became of it,
	 * which an event does not say.
	 */
	procedure_id: string;
	version: number;
	/** As it is now — the service does not version the name. */
	procedure_name: string;
}

export interface AOPExecution {
	id: string;
	/** Radar's own idempotency key for the run. Shown nowhere; logged on errors. */
	execution_key: string;
	work_order_id: number;
	procedure_id: string;
	/**
	 * The procedure's name, as it is now.
	 *
	 * Not versioned on the service's side, so a rename shows on runs that
	 * happened before it — and a deleted procedure still gives its name here,
	 * which is why a finished run can be read at all. It comes with the run so
	 * nothing has to look the procedure up to label one.
	 */
	procedure_name: string;
	/** The published version the run pinned. It cannot change mid-run. */
	version: number;
	started_at: string;
	finished_at: string | null;
	state: AOPExecutionState;
	/**
	 * How it ended and why, lifted from the finished event by the service — so
	 * nothing here has to dig through a snapshot to colour a row. Null while the
	 * run is still going.
	 */
	outcome: AOPExecutionOutcome | null;
	reason: string | null;
	/** The newest event, or null before the first one has landed. */
	latest: AOPEvent | null;
}

/**
 * Reading one run adds the document of the version it pinned.
 *
 * `max_run_seconds` came with it when the service added the procedure-level
 * time limit. Nothing draws any of this yet — the list gives a run's state and
 * its version number, and what a session cannot say today is which plan Radar
 * was actually following. Where that belongs is a design question.
 */
export interface AOPExecutionDetail extends AOPExecution {
	prose: string;
	plan: AOPPlan;
	max_run_seconds: number | null;
}

/**
 * Every list is a cursor page. The cursor is an opaque string for runs and for
 * procedure-wide events, and a sequence number for one run's events, so the
 * type travels with it rather than being assumed.
 *
 * The rows are `data`. They were `items` until the service renamed them, which
 * is the kind of change nothing here can catch on its own: these shapes are
 * typed by us from the OpenAPI document rather than generated from it, so a
 * renamed field arrives as `undefined` and a feed renders empty instead of
 * failing. `pageRows` below is why there is one place to look.
 */
export interface AOPPage<TRow, TCursor> {
	data: TRow[];
	next_cursor: TCursor | null;
}

/** Every page says how many the filters match, not just what is on it. */
export interface AOPCountedPage<TRow, TCursor> extends AOPPage<TRow, TCursor> {
	count: number;
}

/**
 * The rows of a page, or a failure saying the shape is not what we type.
 *
 * Reading `page.data` directly would turn the next rename into an empty feed
 * with nothing said — which is how the last one presented. A page that does not
 * carry its rows is a contract change, so it is raised rather than rendered.
 */
export const pageRows = <TRow>(page: AOPPage<TRow, any>): TRow[] => {
	const rows = page ? page.data : null;

	if (!Array.isArray(rows)) {
		throw new Error('The agent service returned a page without `data`. Its shape has changed.');
	}

	return rows;
};

export type AOPExecutionPage = AOPCountedPage<AOPExecution, string>;

/**
 * Events, from one run or across a procedure, read from the same endpoint —
 * `/execution_events` takes `execution_id`, `procedure_id` and `work_order_id`
 * as filters, so what used to be two shapes is one. Every item names its run
 * and its work order, which a single run's feed simply repeats.
 */
export type AOPEventPage = AOPCountedPage<AOPProcedureEvent, string>;

/** True while a run is held where it is. The service says so directly now. */
export const isExecutionPaused = (execution: AOPExecution): boolean => execution.state === 'paused';
